import { DATASTREAM_MESSAGES } from '@squaredup/constants';
import { DataStreamError } from '@squaredup/data-streams';
import _ from 'lodash';
import React from 'react';
import { TileError } from 'ui/tile/TileError';

type KnownError = {
    /**
     * Heading shown in the UI for this known error
     */
    heading: string;
    /**
     * Text to match the error message against for this error
     */
    match: string;
    /**
     * If specified, text shown instead of the raw error message
     */
    message?: string;
    /**
     * Determines if the "try again" button is visible
     */
    retryable?: boolean;
};

export const knownErrors: KnownError[] = [
    {
        heading: 'Authentication failed',
        match: DATASTREAM_MESSAGES.AUTHENTICATION_FAILED,
        message: DATASTREAM_MESSAGES.AUTHENTICATION_FAILED,
        retryable: true
    },
    {
        heading: 'Resource not found',
        match: DATASTREAM_MESSAGES.NOT_FOUND,
        message: DATASTREAM_MESSAGES.NOT_FOUND
    },
    {
        heading: 'Rate limit reached',
        match: DATASTREAM_MESSAGES.RATE_LIMITTED,
        message: DATASTREAM_MESSAGES.RATE_LIMITTED
    },
    {
        heading: 'Data source error',
        match: DATASTREAM_MESSAGES.INTERNAL_ERROR,
        message: DATASTREAM_MESSAGES.INTERNAL_ERROR,
        retryable: true
    },
    {
        heading: 'Collection not found',
        match: DATASTREAM_MESSAGES.SCOPE_NOT_FOUND,
        message: DATASTREAM_MESSAGES.SCOPE_NOT_FOUND
    },
    {
        heading: 'Data stream not found',
        match: DATASTREAM_MESSAGES.DATASTREAM_NOT_FOUND,
        message: DATASTREAM_MESSAGES.DATASTREAM_NOT_FOUND
    },
    {
        heading: 'Invalid timeframe',
        match: DATASTREAM_MESSAGES.TIMEFRAME_START_AFTER_END,
        message: DATASTREAM_MESSAGES.TIMEFRAME_START_AFTER_END
    },
    {
        heading: 'Data source timed out',
        match: 'Task timed out',
        retryable: true
    }
];

interface DataStreamErrorsProps {
    error: any;
    showDetailedErrors?: boolean;
    /** Show error detail inline instead of in tooltip */
    showDetailInline?: boolean;
    reset?: () => void;
}

/**
 * Transform errors into a standard format
 */
const transformErrors = (error: any): DataStreamError[] => {
    if (!error) {
        return [];
    }

    if (error?.response?.data?.errors) {
        return error.response.data.errors.map((e: string | object) =>
            typeof e === 'string' ? { message: e } : (e as DataStreamError)
        );
    }

    if (error instanceof Error) {
        return [
            {
                message: error.message,
                name: error.name
            }
        ];
    }

    if (Array.isArray(error)) {
        return error.map((e) => ({ message: _.toString(e) }));
    }

    return [{ message: _.toString(error) }];
};

export const DataStreamErrors: React.FC<DataStreamErrorsProps> = ({
    error,
    reset,
    showDetailedErrors = true,
    showDetailInline
}) => {
    const errors = transformErrors(error);
    const primaryError = errors?.[0];
    const errorMessage = primaryError ? knownErrors.find((w) => primaryError.message.includes(w.match)) : null;

    return (
        <TileError
            heading={errorMessage?.heading || 'Something went wrong'}
            articleAnchor={errorMessage?.heading?.toLowerCase().replace(/ /gu, '-')}
            supportCode={error?.response?.headers?.['apigw-requestid']}
            detail={errorMessage?.message || primaryError?.message}
            showDetail={showDetailedErrors}
            showDetailInline={showDetailInline}
            // Errors marked as rrtryable, or unknown errors are resettable
            reset={errorMessage?.retryable || !errorMessage ? reset : undefined}
        />
    );
};
